import React, { useState } from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, FormControl, FormLabel, Input, Textarea, Button, Icon, HStack } from "@chakra-ui/react";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaArrowRight } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission logic would go here
    console.log("Form submitted:", formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/contact"
        fallbackTitle="Contact Us"
        fallbackDescription="Get in touch with our team of experts across solar, finance, and property investment"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              Get In Touch
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Contact Our Team
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              Have questions? We're here to help. Reach out to our experts today.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Contact Form and Info Section */}
      <Container maxW="container.xl" py={20}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12}>
          {/* Contact Form */}
          <Box>
            <Heading size="lg" mb={6} color={brandConfig.colors.primary}>
              Send Us a Message
            </Heading>
            <Text color="gray.600" mb={8}>
              Fill out the form below and we'll get back to you within 24 hours.
            </Text>
            <Box
              as="form"
              onSubmit={handleSubmit}
              p={8}
              bg="white"
              borderRadius="lg"
              boxShadow="xl"
              borderWidth="1px"
              borderColor="gray.200"
            >
              <VStack spacing={6}>
                <FormControl isRequired>
                  <FormLabel color={brandConfig.colors.primary}>Full Name</FormLabel>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    size="lg"
                    borderColor="gray.300"
                    _hover={{ borderColor: brandConfig.colors.primary }}
                    _focus={{ borderColor: brandConfig.colors.primary, boxShadow: `0 0 0 1px ${brandConfig.colors.primary}` }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color={brandConfig.colors.primary}>Email Address</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    size="lg"
                    borderColor="gray.300"
                    _hover={{ borderColor: brandConfig.colors.primary }}
                    _focus={{ borderColor: brandConfig.colors.primary, boxShadow: `0 0 0 1px ${brandConfig.colors.primary}` }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color={brandConfig.colors.primary}>Phone Number</FormLabel>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="0400 000 000"
                    size="lg"
                    borderColor="gray.300"
                    _hover={{ borderColor: brandConfig.colors.primary }}
                    _focus={{ borderColor: brandConfig.colors.primary, boxShadow: `0 0 0 1px ${brandConfig.colors.primary}` }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color={brandConfig.colors.primary}>Message</FormLabel>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your solar, finance, or property needs..."
                    size="lg"
                    rows={6}
                    borderColor="gray.300"
                    _hover={{ borderColor: brandConfig.colors.primary }}
                    _focus={{ borderColor: brandConfig.colors.primary, boxShadow: `0 0 0 1px ${brandConfig.colors.primary}` }}
                  />
                </FormControl>

                <Button
                  type="submit"
                  w="full"
                  size="lg"
                  bg="#C2A878"
                  color={brandConfig.colors.primary}
                  _hover={{ bg: "#b09060", transform: "translateY(-2px)" }}
                  rightIcon={<Icon as={FaArrowRight} />}
                  transition="all 0.2s"
                  fontWeight="bold"
                >
                  Send Message
                </Button>
              </VStack>
            </Box>
          </Box>

          {/* Contact Info Cards */}
          <Box>
            <Heading size="lg" mb={6} color={brandConfig.colors.primary}>
              Other Ways to Reach Us
            </Heading>
            <Text color="gray.600" mb={8}>
              Prefer to reach out directly? Here are other ways to get in touch.
            </Text>

            <VStack spacing={6} align="stretch">
              {/* Phone */}
              <Box
                p={6}
                bg="white"
                borderRadius="lg"
                boxShadow="md"
                borderLeft="4px solid"
                borderColor="#C2A878"
              >
                <HStack spacing={4} mb={3}>
                  <Icon as={FaPhone} w={6} h={6} color={brandConfig.colors.primary} />
                  <Heading size="md" color={brandConfig.colors.primary}>
                    Phone
                  </Heading>
                </HStack>
                <Text color="gray.600" mb={2}>
                  Speak directly with our team
                </Text>
                <Text color={brandConfig.colors.primary} fontWeight="semibold" fontSize="lg">
                  {brandConfig.contact.phone || "+61 XXX XXX XXX"}
                </Text>
              </Box>

              {/* Email */}
              <Box
                p={6}
                bg="white"
                borderRadius="lg"
                boxShadow="md"
                borderLeft="4px solid"
                borderColor="#C2A878"
              >
                <HStack spacing={4} mb={3}>
                  <Icon as={FaEnvelope} w={6} h={6} color={brandConfig.colors.primary} />
                  <Heading size="md" color={brandConfig.colors.primary}>
                    Email
                  </Heading>
                </HStack>
                <Text color="gray.600" mb={2}>
                  Send us an email anytime
                </Text>
                <Text color={brandConfig.colors.primary} fontWeight="semibold" fontSize="lg">
                  {brandConfig.contact.email}
                </Text>
              </Box>

              {/* Address */}
              <Box
                p={6}
                bg="white"
                borderRadius="lg"
                boxShadow="md"
                borderLeft="4px solid"
                borderColor="#C2A878"
              >
                <HStack spacing={4} mb={3}>
                  <Icon as={FaMapMarkerAlt} w={6} h={6} color={brandConfig.colors.primary} />
                  <Heading size="md" color={brandConfig.colors.primary}>
                    Location
                  </Heading>
                </HStack>
                <Text color="gray.600" mb={2}>
                  Serving all of Australia
                </Text>
                <Text color={brandConfig.colors.primary} fontWeight="semibold" fontSize="lg">
                  {brandConfig.contact.address}
                </Text>
              </Box>
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Business Hours Section */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8}>
            <HStack spacing={4}>
              <Icon as={FaClock} w={8} h={8} color={brandConfig.colors.primary} />
              <Heading size="lg" color={brandConfig.colors.primary}>
                Business Hours
              </Heading>
            </HStack>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
              <Box textAlign="center" p={6} bg="white" borderRadius="lg" boxShadow="md">
                <Text fontWeight="bold" color={brandConfig.colors.primary} mb={2}>
                  Monday - Friday
                </Text>
                <Text color="gray.600" fontSize="lg">
                  8:00 AM - 6:00 PM
                </Text>
              </Box>
              <Box textAlign="center" p={6} bg="white" borderRadius="lg" boxShadow="md">
                <Text fontWeight="bold" color={brandConfig.colors.primary} mb={2}>
                  Saturday
                </Text>
                <Text color="gray.600" fontSize="lg">
                  9:00 AM - 4:00 PM
                </Text>
              </Box>
              <Box textAlign="center" p={6} bg="white" borderRadius="lg" boxShadow="md">
                <Text fontWeight="bold" color={brandConfig.colors.primary} mb={2}>
                  Sunday
                </Text>
                <Text color="gray.600" fontSize="lg">
                  Closed
                </Text>
              </Box>
            </SimpleGrid>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              Outside of business hours? Send us a message and we'll respond first thing the next business day.
            </Text>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Contact;
